import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Send, Trash2, Power, PowerOff, Users } from "lucide-react";

const AUDIENCE_LABELS = {
  all_leads: "جميع العملاء المحتملين",
  pending_leads: "العملاء الذين لم يشتروا",
  purchased_leads: "العملاء الذين اشتروا",
  all_members: "جميع أعضاء المنصة",
  paid_subscribers: "مشتركون مدفوعون فقط",
  free_forever_members: "أعضاء مجانيون للأبد",
};

export default function AdminBroadcasts() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [audiences, setAudiences] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", template_key: "", audience: "pending_leads", daily_limit: 50, active: true });

  const load = async () => {
    const [b, t, a] = await Promise.all([
      api.get("/admin/broadcasts"),
      api.get("/admin/emails/templates"),
      api.get("/admin/broadcasts/audiences"),
    ]);
    setBroadcasts(b.data);
    setTemplates(t.data);
    setAudiences(a.data);
  };

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const create = async () => {
    if (!form.name || !form.template_key) { toast.error("الاسم والقالب مطلوبان"); return; }
    setBusy(true);
    try {
      await api.post("/admin/broadcasts", form);
      toast.success("تم إنشاء الحملة");
      setCreateOpen(false);
      setForm({ name: "", template_key: "", audience: "pending_leads", daily_limit: 50, active: true });
      await load();
    } catch (e) { toast.error(extractError(e, "فشل الإنشاء")); }
    finally { setBusy(false); }
  };

  const toggle = async (id) => { await api.patch(`/admin/broadcasts/${id}/toggle`); await load(); };
  const remove = async (id) => {
    if (!confirm("حذف هذه الحملة نهائيًا مع جميع سجلات المستقبلين؟")) return;
    await api.delete(`/admin/broadcasts/${id}`); await load();
  };

  return (
    <div className="space-y-6" data-testid="admin-broadcasts">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold">الحملات البريدية</h1>
          <p className="text-foreground/60 mt-2 text-sm">أرسل رسالة واحدة لمجموعة كبيرة بحد يومي آمن لحماية سمعة نطاقك.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="btn-new-broadcast"><Plus className="h-4 w-4 me-2" /> حملة جديدة</Button>
      </div>

      {/* Broadcast list */}
      <div className="space-y-3">
        {broadcasts.length === 0 && <Card className="p-8 text-center text-foreground/50">لا توجد حملات بعد.</Card>}
        {broadcasts.map((b) => {
          const pct = b.total_recipients ? Math.round(((b.sent_count + b.failed_count) / b.total_recipients) * 100) : 0;
          return (
            <Card key={b.id} className="p-4 space-y-3" data-testid={`broadcast-${b.id}`}>
              <div className="flex justify-between items-start flex-wrap gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold flex items-center flex-wrap gap-2">
                    {b.name}
                    {b.active
                      ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-800">يعمل</span>
                      : <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">متوقف</span>}
                  </div>
                  <div className="text-xs text-foreground/60 mt-1">
                    قالب: {b.template_key} • جمهور: {AUDIENCE_LABELS[b.audience] || b.audience} • حد يومي: {b.daily_limit}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant={b.active ? "outline" : "default"} onClick={() => toggle(b.id)}>
                    {b.active ? <><PowerOff className="h-4 w-4 me-1" /> إيقاف</> : <><Power className="h-4 w-4 me-1" /> تشغيل</>}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(b.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </div>
              </div>
              <Progress value={pct} />
              <div className="grid grid-cols-4 gap-2 text-xs">
                <Stat label="المجموع" value={b.total_recipients} />
                <Stat label="مُرسل" value={b.sent_count} color="text-green-700" />
                <Stat label="متبقٍ" value={b.pending_count} color="text-yellow-700" />
                <Stat label="فشل" value={b.failed_count} color="text-red-600" />
              </div>
              <div className="text-[11px] text-foreground/50">أُرسل اليوم: {b.sent_today} / {b.daily_limit}</div>
            </Card>
          );
        })}
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent dir="rtl" className="max-w-md">
          <DialogHeader><DialogTitle>حملة بريدية جديدة</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>اسم الحملة</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="br-name" />
            </div>
            <div>
              <Label>القالب</Label>
              <Select value={form.template_key} onValueChange={(v) => setForm({ ...form, template_key: v })}>
                <SelectTrigger data-testid="br-template"><SelectValue placeholder="اختر قالبًا" /></SelectTrigger>
                <SelectContent>
                  {templates.filter((t) => !t.disabled).map((t) => <SelectItem key={t.key} value={t.key}>{t.subject || t.key}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>الجمهور</Label>
              <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                <SelectTrigger data-testid="br-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending_leads">لم يشتروا ({audiences.pending_leads ?? 0})</SelectItem>
                  <SelectItem value="all_leads">جميع العملاء المحتملين ({audiences.all_leads ?? 0})</SelectItem>
                  <SelectItem value="purchased_leads">اشتروا ({audiences.purchased_leads ?? 0})</SelectItem>
                  <SelectItem value="all_members">أعضاء المنصة ({audiences.all_members ?? 0})</SelectItem>
                  <SelectItem value="paid_subscribers">مشتركون مدفوعون فقط ({audiences.paid_subscribers ?? 0})</SelectItem>
                  <SelectItem value="free_forever_members">أعضاء مجانيون للأبد ({audiences.free_forever_members ?? 0})</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>حد الإرسال اليومي (لحماية سمعة النطاق)</Label>
              <Input type="number" min={1} max={1000} value={form.daily_limit} onChange={(e) => setForm({ ...form, daily_limit: parseInt(e.target.value) || 50 })} data-testid="br-limit" />
              <p className="text-[10px] text-foreground/50 mt-1">يوصى بـ 50 يوميًا في البداية. سيتم إرسال هذا العدد يوميًا حتى تكتمل الحملة.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>إلغاء</Button>
            <Button onClick={create} disabled={busy} data-testid="btn-create-broadcast"><Send className="h-4 w-4 me-2" /> إنشاء وبدء</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Stat({ label, value, color = "text-foreground" }) {
  return <div className="rounded-md bg-black/[0.03] p-2 text-center"><div className="text-[10px] text-foreground/60">{label}</div><div className={`font-semibold ${color}`}>{value}</div></div>;
}
